// src/pages/ProfilePage.js
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import algosdk from 'algosdk';
import { PeraWalletConnect } from '@perawallet/connect';

import apiClient from '../api';
import '../styles/ProfilePage.css';

const peraWallet = new PeraWalletConnect();

const shorten = (addr) => (addr && addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-6)}` : addr || '');

function SubscriptionSummary({ sub }) {
  if (!sub || sub.status === 'none' || !sub.tier) {
    return (
      <div className="profile-sub-row">
        <span className="profile-sub-pill profile-sub-pill-none">No active subscription</span>
        <Link to="/subscription" className="profile-link">Choose a plan</Link>
      </div>
    );
  }
  const expires = sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—';
  const pillCls = {
    active: 'profile-sub-pill-active',
    demo: 'profile-sub-pill-demo',
    read_only: 'profile-sub-pill-readonly',
    expired: 'profile-sub-pill-expired',
  }[sub.status] || 'profile-sub-pill-none';
  return (
    <div className="profile-sub-row">
      <span className={`profile-sub-pill ${pillCls}`}>
        {sub.tier?.toUpperCase()} · {sub.status}
      </span>
      <span className="profile-sub-meta">
        User cap: <strong>{sub.user_cap}</strong> · Expires: <strong>{expires}</strong>
        {sub.days_remaining != null && ` (${sub.days_remaining} day(s) left)`}
      </span>
      <Link to="/subscription" className="profile-link">Manage</Link>
    </div>
  );
}

function ProfilePage({ isAuthenticated }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/admin/me/profile');
      setProfile(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  const connectWallet = async () => {
    setError('');
    setMessage('');
    setWorking(true);
    let address = '';
    try {
      try {
        const accounts = await peraWallet.reconnectSession();
        address = accounts?.[0] || '';
      } catch {
        address = '';
      }
      if (!address) {
        try { await peraWallet.disconnect(); } catch { /* ignore */ }
        const accounts = await peraWallet.connect();
        if (!accounts?.length) throw new Error('Pera Wallet connection cancelled.');
        address = accounts[0];
      }

      setMessage('Requesting challenge from server...');
      const ch = await apiClient.post('/admin/me/wallet/challenge', { address });
      const { unsigned_txn_b64, nonce } = ch.data;

      setMessage('Open Pera Wallet to sign the link challenge...');
      const bytes = Uint8Array.from(atob(unsigned_txn_b64), (c) => c.charCodeAt(0));
      const txn = algosdk.decodeUnsignedTransaction(bytes);
      const signedTxns = await peraWallet.signTransaction([[{ txn, signers: [address] }]]);
      const signed_b64 = btoa(String.fromCharCode(...signedTxns[0]));

      setMessage('Verifying signature with the server...');
      const linked = await apiClient.post('/admin/me/wallet/link', {
        signed_txn_b64: signed_b64,
        nonce,
      });
      setProfile(linked.data);
      setMessage('Wallet linked successfully.');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Unknown error';
      setError(`Could not link wallet: ${detail}`);
      setMessage('');
    } finally {
      setWorking(false);
    }
  };

  const unlinkWallet = async () => {
    if (!window.confirm('Unlink the current wallet from this account?')) return;
    setError('');
    setMessage('');
    setWorking(true);
    try {
      const r = await apiClient.delete('/admin/me/wallet');
      setProfile(r.data);
      try { await peraWallet.disconnect(); } catch { /* ignore */ }
      setMessage('Wallet unlinked.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setWorking(false);
    }
  };

  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (loading) return <div className="profile-page"><p>Loading profile...</p></div>;
  if (!profile) return <div className="profile-page"><p className="profile-error">{error || 'Profile unavailable.'}</p></div>;

  return (
    <div className="profile-page">
      <h1>Profile</h1>
      <p className="profile-subtitle">Your account details and linked wallet.</p>

      <section className="profile-card">
        <h2>Account</h2>
        <div className="profile-field">
          <label>Email</label>
          {profile.email ? (
            <div className="profile-value-row">
              <span>{profile.email}</span>
              {profile.is_confirmed
                ? <span className="profile-badge profile-badge-ok">Confirmed</span>
                : <span className="profile-badge profile-badge-warn">Not confirmed</span>}
              {profile.google_linked && <span className="profile-badge profile-badge-info">Google</span>}
            </div>
          ) : (
            <span className="profile-muted">No email on file (wallet-only account).</span>
          )}
        </div>
      </section>

      <section className="profile-card">
        <h2>Pera Wallet</h2>
        {profile.pera_address ? (
          <>
            <div className="profile-field">
              <label>Linked address</label>
              <div className="profile-value-row">
                <code className="profile-address" title={profile.pera_address}>
                  {shorten(profile.pera_address)}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(profile.pera_address)}
                  className="profile-link-btn"
                >
                  Copy
                </button>
              </div>
            </div>
            <button
              type="button"
              className="profile-btn profile-btn-danger"
              onClick={unlinkWallet}
              disabled={working}
            >
              Unlink wallet
            </button>
          </>
        ) : (
          <>
            <p className="profile-muted">
              Link a Pera wallet to issue on-chain ASA rewards. We never read your seed phrase —
              we only verify a signature over a server-issued challenge.
            </p>
            <button
              type="button"
              className="profile-btn profile-btn-primary"
              onClick={connectWallet}
              disabled={working}
            >
              {working ? 'Connecting...' : 'Connect Pera Wallet'}
            </button>
          </>
        )}
      </section>

      <section className="profile-card">
        <h2>Subscription</h2>
        <SubscriptionSummary sub={profile.subscription} />
      </section>

      {message && <p className="profile-info">{message}</p>}
      {error && <p className="profile-error">{error}</p>}
    </div>
  );
}

export default ProfilePage;
